import { Box, Grid, Stack, Typography, styled } from "@mui/material";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IconEditor } from "@/assets/icons";
import { CollapsibleCard } from "@/components/elements/CollapsibleCard";
import { InnerBox } from "@/components/elements/InnerBox";
import { Button } from "@/modules/irohana-components/elements/Button";
import { Divider } from "@/modules/irohana-components/elements/Divider";
import { FooterActionBar } from "@/modules/irohana-components/elements/FooterActionBar";
import { MenuNavigate } from "@/modules/irohana-components/elements/MenuNavigate";
import type { TMenuContent } from "@/modules/irohana-components/elements/MenuNavigate/MenuNavigate";
import { ResultField } from "@/modules/irohana-components/elements/ResultField";
import { AppRoutes } from "@/routes/config";
import templatePreview from "@/features/organization/invoice/assets/invoice-template-preview.png";
import { InvoiceNavHeader } from "@/features/organization/invoice/components/InvoiceNavHeader";
import { FlowStepsBox } from "@/features/organization/invoice/components/invoice-form/FlowStepsBox";
import { BILLING_FLOW_STEPS } from "@/features/organization/invoice/constants/invoiceEnums";
import { InvoicePreview } from "@/features/organization/invoice/components/invoice-form/InvoicePreview";
import { TInvoiceDetail } from "@/features/organization/invoice/types/InvoiceDetail";
import { divider, text } from "@/theme/colors";

/** Read-only row: "選択中のフォーマット：{format}" with a bottom divider. */
const SelectedFormatRow = ({ format }: { format: string }) => (
  <Stack gap={1}>
    <Typography variant="body14" color={text.primary}>
      <Typography component="span" variant="body14" color={text.secondary}>
        選択中のフォーマット：
      </Typography>
      {format}
    </Typography>
    <Divider color={divider.middle} sx={{ mt: 1 }} />
  </Stack>
);

type TInvoiceDetailViewProps = {
  detail: TInvoiceDetail;
};

/** 請求設定 (invoice settings) — read-only 確認 view. */
export const InvoiceDetailView = ({ detail }: TInvoiceDetailViewProps) => {
  const navigate = useNavigate();

  const contents: TMenuContent[] = useMemo(
    () => [
      {
        title: "請求期日情報",
        content: (
          <CollapsibleCard title="請求期日情報">
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <ResultField label="請求月" value={detail.billingMonth} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResultField label="請求日" value={detail.billingDay} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResultField label="支払締切（月）" value={detail.paymentCutoffMonth} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResultField label="支払締切（日）" value={detail.paymentCutoffDay} />
              </Grid>
            </Grid>
          </CollapsibleCard>
        ),
      },
      {
        title: "入退社月情報",
        content: (
          <CollapsibleCard title="入退社月情報">
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <ResultField label="入社月" value={detail.joinMonth} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResultField label="退職月" value={detail.leaveMonth} />
              </Grid>
            </Grid>
          </CollapsibleCard>
        ),
      },
      {
        title: "一時帰国・休職情報",
        content: (
          <CollapsibleCard title="一時帰国・休職情報">
            <Grid container columnSpacing={3} rowSpacing={2}>
              <Grid item xs={12} md={6}>
                <ResultField label="一時帰国" value={detail.temporaryReturn} />
              </Grid>
              <Grid item xs={12} md={6}>
                <ResultField label="休職" value={detail.leaveOfAbsence} />
              </Grid>
            </Grid>
          </CollapsibleCard>
        ),
      },
      {
        title: "請求書番号設定",
        content: (
          <CollapsibleCard title="請求書番号設定">
            <Stack gap={2}>
              <SelectedFormatRow format={detail.invoiceNumberFormat} />
              <InvoicePreview value={detail.invoiceNumberPreview} />
            </Stack>
          </CollapsibleCard>
        ),
      },
      {
        title: "テンプレート設定",
        content: (
          <CollapsibleCard title="テンプレート設定">
            <Stack gap={1} width={213}>
              <Thumbnail>
                <img src={templatePreview} alt={detail.templateName} width={213} />
              </Thumbnail>
              <Box textAlign="center" py={1}>
                <Typography variant="body14Semi" color={text.primary}>
                  {detail.templateName}
                </Typography>
              </Box>
            </Stack>
          </CollapsibleCard>
        ),
      },
      {
        title: "請求フロー設定",
        content: (
          <CollapsibleCard title="請求フロー設定">
            <Stack gap={0.5}>
              <Typography variant="cap12" color={text.primary}>
                適用中のフロー
              </Typography>
              <FlowStepsBox steps={BILLING_FLOW_STEPS} />
            </Stack>
          </CollapsibleCard>
        ),
      },
      {
        title: "システム既定担当者",
        content: (
          <CollapsibleCard title="システム既定担当者">
            <ResultField label="既定担当者" value={detail.defaultAssignee} />
          </CollapsibleCard>
        ),
      },
    ],
    [detail],
  );

  return (
    <>
      <InnerBox showDivider={false} sx={{ borderRadius: "0 0 8px 8px" }}>
        <MenuNavigate contents={contents} menuPrefix={<InvoiceNavHeader />} />
      </InnerBox>

      <Box mt={3}>
        <FooterActionBar>
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outline" startIcon={<IconEditor />} onClick={() => navigate(AppRoutes.organizationInvoiceEdit)}>
              編集
            </Button>
          </Stack>
        </FooterActionBar>
      </Box>
    </>
  );
};

const Thumbnail = styled(Box)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0px 4px 10px 0px rgba(0, 0, 0, 0.15);
  line-height: 0;
  img {
    display: block;
    width: 100%;
    height: auto;
  }
`;
